<template>
  <b-container fluid class="pt-3 px-5 flex-grow-1 d-flex flex-column">
    <b-alert
        :show="isFiltered && showFilterAlert"
        variant="info"
        fade
        class="d-flex align-items-center"
    >
      <CustomIcon icon="info-alt" class="mr-3" />

      <div>
        You're viewing
        {{ $store.getters.propertyTenure || '' }} based on {{ $store.getters.dueDateType.name }}.
        <a href="#" class="alert-link" @click="showFilterAlert = false">I understand.</a>
      </div>
    </b-alert>

    <!-- top row -->
    <b-row>
      <b-col cols="12" class="pt-1">
        <riskhub-vue-element-loading :active="loading" :is-full-screen="true" />
        <stat-panel v-show="!loading" class="mb-2" :data="statPanel"></stat-panel>
      </b-col>
    </b-row>

    <!-- middle row -->
    <b-row>
      <b-col cols="12" lg="8" class="px-2 mb-3">
        <r-card
                title="FRA Upcoming"
                class="h-100"
                style="min-height: 500px;"
        >
          <template #title>
            <div class="d-flex">
              <h4 class="card-title">RA Upcoming</h4>
              <portal-target name="chart-select" class="ml-auto"></portal-target>
            </div>
          </template>
          <property-multi-chart v-if="dataLoaded"
                                :data="chartData"
                                :loading="dataLoading"
                                :interval="interval"
                                @reload="updateData"
          />
          <template #footer>
            <b-card-footer><portal-target name="filters"></portal-target></b-card-footer>
          </template>
        </r-card>
      </b-col>
      <b-col cols="12" lg="4" class="px-2 mb-3">
        <r-card
            title="Jobs Summary"
            class="h-100"
        >
          <property-count-hierarchy v-if="dataLoaded" :counts="counts" />
        </r-card>
      </b-col>

    </b-row>

    <!-- bottom row -->
    <b-row>
      <b-col cols="12" lg="8" class="px-2">
        <r-card
            :title="propertyTypeBreakdown.label"
            :info="propertyTypeBreakdown.info"
            class="h-100"
            title-class="mb-0"
        >
          <stat-box :value="propertyTypeBreakdown.value"
                    :color="propertyTypeBreakdown.color"
                    size="small"
                    :to="propertyTypeBreakdown.to"
          />
          <breakdown-panel v-if="dataLoaded" class="mt-2" :data="propertyTypeBreakdown.breakdown" />
        </r-card>
      </b-col>
      <b-col cols="12" lg="4" class="px-2">
        <r-card
            title="Quicklinks"
            class="h-100"
        >
          <b-row class="d-flex h-100">
            <b-col class="align-self-center">
              <property-quicklinks />
            </b-col>
          </b-row>
        </r-card>
      </b-col>

    </b-row>
  </b-container>
</template>

<script>
import RCard from '@/components/Layout/RCard';
import StatPanel from '@/components/Dashboard/StatPanel';
import StatBox from '@/components/Dashboard/StatBox';
import PropertyCountHierarchy from '@/components/Property/Dashboard/PropertyCountHierarchy';
import BreakdownPanel from '@/components/Dashboard/BreakdownPanel';
import PropertyQuicklinks from '@/components/Property/Dashboard/PropertyQuicklinks';
import Property from '@/services/Property/Property';
import PropertyMultiChart from '@/components/Property/Dashboard/PropertyMultiChart';
import PropertyAssessmentType from '@/services/Property/PropertyAssessmentType';
import PropertyAssessmentTypeStatus from '@/services/Property/PropertyAssessmentTypeStatus';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import CustomIcon from '@/components/Custom/CustomIcon';

dayjs.extend(isSameOrAfter);

export default {
  name: 'PropertyDashboard',
  components: {
    CustomIcon,
    RCard,
    StatPanel,
    StatBox,
    PropertyCountHierarchy,
    BreakdownPanel,
    PropertyQuicklinks,
    PropertyMultiChart,
  },
  data() {
    return {
      loading: false,
      activeChart: 'bar',
      counts: null,
      interval: 'month',
      chartData: null,
      dataLoading: false,
      dataLoaded: false,
      statPanel: {
        group1: {
          label: 'Compliance',
          value: 0,
          info: 'This is the percentage of ‘Live Properties‘ that have a compliant status. ‘Upcoming‘ and ‘RA Not Required‘ are compliant and ‘Overdue‘ are non-compliant.',
        },
        group2: {
          label: 'Live Properties',
          value: 0,
          to: {
            name: 'programme',
          },
        },
        group3: {
          label: 'Overdue',
          value: 0,
          to: {
            name: 'programme',
            query: { overdue: 1 },
          },
          subgroups: [
            {
              label: '90+ DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 7 },
              },
            },
            {
              label: '61-90 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 6 },
              },
            },
            {
              label: '31-60 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 5 },
              },
            },
            {
              label: '1-30 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 4 },
              },
            },
          ],
        },
        group4: {
          label: 'Upcoming',
          value: 0,
          to: {
            name: 'programme',
            query: { overdue: 2 },
          },
          subgroups: [
            {
              label: '1-30 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 8 },
              },
            },
            {
              label: '31-60 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 9 },
              },
            },
            {
              label: '61-90 DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 10 },
              },
            },
            {
              label: '90+ DAYS',
              value: 0,
              to: {
                name: 'programme',
                query: { overdue: 11 },
              },
            },
          ],
        },
      },
      propertyTypeBreakdown: {
        label: 'RA Not Required',
        value: 0,
        info: 'These properties are classed as compliant because the compliance responsibility lies outside your jurisdiction.',
        color: '#00D2B4',
        to: {
          name: 'programme',
          query: { notRequired: 1 },
          params: { title: 'RA Not Required' },
        },
        breakdown: [
          {
            label: 'INCLUDED IN OTHER RA',
            value: 0,
            icon: 'includedinotherfra',
            bgColor: '#34668C',
            to: {
              name: 'programme',
              query: {
                complianceStatus: PropertyAssessmentTypeStatus.INCLUDED_IN_OTHER_RA(),
                catB: '0',
              },
              params: { title: 'Included in Other RA' },
            },
          },
          {
            label: 'CATEGORY B',
            value: 0,
            icon: 'catB',
            bgColor: '#99B3C5',
            to: {
              name: 'programme',
              query: { catB: '1' },
              params: { title: 'Category B' },
            },
          },
          {
            label: 'VOID',
            value: 0,
            icon: 'void',
            bgColor: '#FFA600',
            to: {
              name: 'programme',
              query: {
                complianceStatus: PropertyAssessmentTypeStatus.VOID(),
                catB: '0',
              },
            },
          },
          {
            label: 'MANAGING AGENT',
            value: 0,
            icon: 'managingagent',
            bgColor: '#E87D5C',
            to: {
              name: 'programme',
              query: {
                complianceStatus: PropertyAssessmentTypeStatus.MANAGING_AGENT(),
                catB: '0',
              },
              params: { title: 'Managing Agent' },
            },
          },
          {
            label: 'UNDER REFURBISHMENT',
            value: 0,
            icon: 'underrefurbishment',
            bgColor: '#638BC2',
            to: {
              name: 'programme',
              query: {
                complianceStatus: PropertyAssessmentTypeStatus.UNDER_REFURBISHMENT(),
                catB: '0',
              },
              params: { title: 'Under Refurbishment' },
            },
          },
          {
            label: 'N/A',
            value: 0,
            icon: 'settings',
            bgColor: '#477A71',
            to: {
              name: 'programme',
              query: {
                complianceStatus: PropertyAssessmentTypeStatus.NOT_APPLICABLE(),
                catB: '0',
              },
            },
          },
        ],
      },
      showFilterAlert: true,
    };
  },
  computed: {
    isFiltered() {
      return !!this.$store.getters.dueDateType;
    },
    raNotRequired() {
      let count = 0;
      this.propertyTypeBreakdown.breakdown.forEach((item) => {
        count += item.value;
      });

      return count;
    },
  },
  created() {

  },
  mounted() {
    this.fetchData();
  },
  watch: {
    '$store.getters.complianceTypeId': function () {
      window.location.reload();
    },
    '$store.getters.propertyTenure': function (value) {
      if (value) {
        window.location.reload();
      }
    },
    '$store.getters.dueDateType': function (value) {
      if (value) {
        window.location.reload();
      }
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.dataLoading = true;

      try {
        // Get results for FIRE RISK only
        this.$store.dispatch('refreshCounts', this.$store.getters.complianceTypeId);

        await this.getCounts();
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
        this.dataLoading = false;
      }
    },
    async getCounts() {
      const { data } = await Property.getExtendedCounts(
        this.$store.getters.complianceTypeId,
        this.interval,
        this.$store.getters.propertyTenure,
        this.$store.getters.dueDateType.value,
      );
      this.counts = data.data;
      this.setStatPanelData();
      this.setBreakdownPanelData();
      this.setChartData();
      this.dataLoaded = true;
    },
    setChartData() {
      const now = dayjs().startOf(this.interval);
      this.chartData = this.counts.breakdown.filter((obj) => {
        const start = dayjs(obj.startDate).startOf(this.interval);
        return dayjs(start).isSameOrAfter(now, 'day');
      });
    },
    setStatPanelData() {
      const {
        overdue, upcoming, total, compliance,
      } = this.counts;

      // Compliance
      this.statPanel.group1.value = compliance;

      // Total
      this.statPanel.group2.value = total;

      // overdue
      const overdueTotal = overdue['90+ days'] + overdue['61-90 days'] + overdue['31-60 days'] + overdue['1-30 days'];
      this.statPanel.group3.value = overdueTotal;
      this.statPanel.group3.subgroups[0].value = overdue['90+ days'];
      this.statPanel.group3.subgroups[1].value = overdue['61-90 days'];
      this.statPanel.group3.subgroups[2].value = overdue['31-60 days'];
      this.statPanel.group3.subgroups[3].value = overdue['1-30 days'];

      // upcoming
      const upcomingTotal = upcoming['1-30 days'] + upcoming['31-60 days'] + upcoming['61-90 days'] + upcoming['90+ days'];
      this.statPanel.group4.value = upcomingTotal;
      this.statPanel.group4.subgroups[0].value = upcoming['1-30 days'];
      this.statPanel.group4.subgroups[1].value = upcoming['31-60 days'];
      this.statPanel.group4.subgroups[2].value = upcoming['61-90 days'];
      this.statPanel.group4.subgroups[3].value = upcoming['90+ days'];
    },
    setBreakdownPanelData() {
      const { complianceStatus, catB } = this.counts;

      // Breakdown
      this.propertyTypeBreakdown.breakdown[0].value = complianceStatus['inc other ra'];
      this.propertyTypeBreakdown.breakdown[1].value = catB;
      this.propertyTypeBreakdown.breakdown[2].value = complianceStatus.void;
      this.propertyTypeBreakdown.breakdown[3].value = complianceStatus['man agent'];
      this.propertyTypeBreakdown.breakdown[4].value = complianceStatus['under refurb'];
      this.propertyTypeBreakdown.breakdown[5].value = complianceStatus['n a'];

      // Total
      this.propertyTypeBreakdown.value = this.raNotRequired;
    },
    async updateData(interval) {
      this.interval = interval;

      this.dataLoading = true;

      try {
        await this.getCounts();
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.dataLoading = false;
      }
    },
  },
};
</script>

<style scoped>

</style>
